import React from 'react';
import { Mic, Square, Play, Trash2, Send, Languages, Activity, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';

export function VoiceReporter({ onComplete }: { onComplete: (blob: string) => void }) {
  const [isRecording, setIsRecording] = React.useState(false);
  const [timer, setTimer] = React.useState(0);
  const [recordedBlob, setRecordedBlob] = React.useState<string | null>(null);
  const [detectedLanguage, setDetectedLanguage] = React.useState<string | null>(null);

  React.useEffect(() => {
    let interval: any;
    if (isRecording) {
      interval = setInterval(() => setTimer(t => t + 1), 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  const startRecording = () => {
    setIsRecording(true);
    setTimer(0);
    setRecordedBlob(null);
    setDetectedLanguage(null);
  };

  const stopRecording = () => {
    setIsRecording(false);
    setRecordedBlob('simulated-voice-note');
    
    // Simulate AI language detection
    setTimeout(() => {
      const manualLang = localStorage.getItem('sentlo_manual_lang');
      const dialects = ['Pidgin', 'Yoruba', 'Igbo', 'Hausa', 'French'];
      
      // If user has a manual language set, use it as the "detected" one to avoid frustration
      // Otherwise, pick a random one but ENSURE consistency between state and toast
      let detected = manualLang === 'gh' ? 'Pidgin' : 
                     manualLang === 'yo' ? 'Yoruba' :
                     manualLang === 'ig' ? 'Igbo' :
                     manualLang === 'ha' ? 'Hausa' :
                     manualLang === 'fr' ? 'French' :
                     dialects[Math.floor(Math.random() * dialects.length)];
      
      setDetectedLanguage(detected);
      toast.success('Language Detected', {
        description: `Sentlo AI identified your report as ${detected}. Transcribing now...`
      });
    }, 1000);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-zinc-900 rounded-[32px] p-8 text-white text-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <Activity className="w-full h-full text-blue-500" />
      </div>

      {!recordedBlob ? (
        <div className="relative z-10 space-y-6">
          <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto relative">
            <AnimatePresence>
              {isRecording && (
                <motion.div 
                  initial={{ scale: 1, opacity: 0.5 }}
                  animate={{ scale: 1.5, opacity: 0 }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                  className="absolute inset-0 rounded-full bg-blue-500"
                />
              )}
            </AnimatePresence>
            <Mic className={`w-10 h-10 ${isRecording ? 'text-blue-400' : 'text-zinc-500'}`} />
          </div>

          <div>
            <h3 className="text-xl font-black italic uppercase mb-2">Voice Report</h3>
            <p className="text-xs text-zinc-400 leading-relaxed max-w-[200px] mx-auto">
              Speak in your local dialect. Our AI transcribes Pidgin, Yoruba, Igbo, and Hausa.
            </p>
          </div>

          <div className="text-3xl font-black tabular-nums tracking-tighter mb-8">
            {formatTime(timer)}
          </div>

          <button 
            onClick={isRecording ? stopRecording : startRecording}
            className={`w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${isRecording ? 'bg-red-600 text-white shadow-lg shadow-red-600/20' : 'bg-white text-zinc-900 shadow-lg shadow-white/10'}`}
          >
            {isRecording ? 'Stop Recording' : 'Start Recording'}
          </button>
        </div>
      ) : (
        <div className="relative z-10 space-y-6">
          <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center text-green-400">
                <Play className="w-5 h-5 fill-current" />
              </div>
              <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 5 }}
                  className="h-full bg-green-500"
                />
              </div>
              <span className="text-[10px] font-black">{formatTime(timer)}</span>
            </div>

            <div className="flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <Sparkles className="w-3.5 h-3.5 text-blue-400" />
              <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">
                Detected: {detectedLanguage || 'Detecting...'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button 
              onClick={() => setRecordedBlob(null)}
              className="bg-zinc-800 text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest border border-zinc-700 active:scale-95 transition-all"
            >
              Discard
            </button>
            <button 
              onClick={() => {
                onComplete(recordedBlob);
                toast.success('Report Submitted', { description: 'Your voice report is being processed and will appear in the feed shortly.' });
              }}
              className="bg-green-600 text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-green-600/20 active:scale-95 transition-all"
            >
              Submit Report
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
